import { useState, useCallback, useEffect, useRef } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RelationsTable } from "./RelationsTable";
import { GenerateButton } from "./GenerateButton";
import { TabHelperBox } from "./TabHelperBox";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Info, AlertTriangle, Loader2, Trash2 } from "lucide-react";
import type {
  ThematicMap,
  ThematicNodeRelation,
  ThematicNodeWithEntity,
  NewThematicNodeRelation,
  PromptStep,
  LLMRelationOutput,
} from "@/types/thematicMaps";

interface RelationsSectionProps {
  map: ThematicMap;
  relations: ThematicNodeRelation[];
  relationsLoading: boolean;
  nodes: ThematicNodeWithEntity[];
  generating: boolean;
  currentStep: PromptStep | null;
  runRelationGeneration: (
    map: ThematicMap,
    nodes: ThematicNodeWithEntity[],
    onProgress?: (msg: string) => void,
  ) => Promise<LLMRelationOutput[] | null>;
  onCreateRelation: (
    data: Omit<NewThematicNodeRelation, "user_id" | "map_id">,
  ) => Promise<ThematicNodeRelation | null>;
  onCreateRelationsBulk: (
    data: Omit<NewThematicNodeRelation, "user_id" | "map_id">[],
  ) => Promise<ThematicNodeRelation[]>;
  onDeleteRelation: (id: string) => Promise<boolean>;
  onDeleteAllRelations: () => Promise<boolean>;
}

const HELPER_ITEMS = [
  "<strong>parent_of</strong> — node A jest rodzicem node B (hub → podstrona).",
  "<strong>sibling</strong> — nodes na tym samym poziomie, ten sam temat.",
  "<strong>prerequisite</strong> — trzeba przeczytac A zeby zrozumiec B.",
  "<strong>next_step</strong> — B to logiczny nastepny krok po A.",
  "<strong>comparison</strong> — A i B porownuja podobne tematy.",
  "<strong>glossary_ref / entity_ref</strong> — A odwoluje sie do slownika/encji z B.",
  "<strong>Waga</strong> — 1 (slaba) do 10 (bardzo silna) — wplywa na internal linking.",
];

export function RelationsSection({
  map,
  relations,
  relationsLoading,
  nodes,
  generating,
  currentStep,
  runRelationGeneration,
  onCreateRelation,
  onCreateRelationsBulk,
  onDeleteRelation,
  onDeleteAllRelations,
}: RelationsSectionProps) {
  const { toast } = useToast();
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Elapsed timer
  useEffect(() => {
    if (statusMessage) {
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      setElapsed(0);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [statusMessage]);

  const hasNodes = nodes.length > 0;

  // Relation generation
  const handleGenerateRelations = useCallback(async () => {
    setStatusMessage("Przygotowywanie generowania relacji...");
    try {
      const result = await runRelationGeneration(map, nodes, setStatusMessage);
      if (!result) return;

      setStatusMessage(
        `Wygenerowano ${result.length} relacji. Rozwiazywanie slugow...`,
      );

      const slugToNodeId = new Map(nodes.map((n) => [n.slug, n.id]));
      const existingRelKeys = new Set(
        relations.map(
          (r) => `${r.from_node_id}|${r.to_node_id}|${r.relation_type}`,
        ),
      );

      const resolved: Array<{
        from_node_id: string;
        to_node_id: string;
        relation_type: string;
        weight: number;
      }> = [];
      let skippedSlug = 0;
      let skippedSelf = 0;
      let skippedDupe = 0;

      for (const rel of result) {
        const fromSlug = rel.from_slug.replace(/^\//, "").trim();
        const toSlug = rel.to_slug.replace(/^\//, "").trim();
        const fromId = slugToNodeId.get(fromSlug);
        const toId = slugToNodeId.get(toSlug);

        if (!fromId || !toId) {
          skippedSlug++;
          continue;
        }
        if (fromId === toId) {
          skippedSelf++;
          continue;
        }

        const key = `${fromId}|${toId}|${rel.relation_type}`;
        if (existingRelKeys.has(key)) {
          skippedDupe++;
          continue;
        }

        resolved.push({
          from_node_id: fromId,
          to_node_id: toId,
          relation_type: rel.relation_type,
          weight: Math.max(0, Math.min(10, Math.round(rel.weight))),
        });
      }

      const totalSkipped = skippedSlug + skippedSelf + skippedDupe;

      if (resolved.length === 0) {
        const reasons = [];
        if (skippedSlug > 0) reasons.push(`${skippedSlug} nieznane slugi`);
        if (skippedSelf > 0) reasons.push(`${skippedSelf} self-relations`);
        if (skippedDupe > 0) reasons.push(`${skippedDupe} duplikaty`);
        toast({
          title: "Brak nowych relacji",
          description:
            reasons.length > 0
              ? `Pominieto ${totalSkipped}: ${reasons.join(", ")}`
              : "Wszystkie relacje zostaly pominiete",
          variant: "destructive",
        });
        return;
      }

      setStatusMessage(`Zapisywanie ${resolved.length} relacji do bazy...`);
      await onCreateRelationsBulk(resolved as any);

      toast({
        title: "Sukces",
        description:
          `Zapisano ${resolved.length} relacji` +
          (totalSkipped > 0 ? ` (pominieto ${totalSkipped})` : ""),
      });
    } finally {
      setStatusMessage(null);
    }
  }, [
    map,
    nodes,
    relations,
    runRelationGeneration,
    onCreateRelationsBulk,
    toast,
  ]);

  return (
    <div className="space-y-4">
      <TabHelperBox title="Typy relacji i ich znaczenie" items={HELPER_ITEMS} />

      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">
          Relacje ({relations.length})
        </h3>
        <div className="flex items-center gap-2">
          {relations.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => {
                if (
                  confirm(
                    `Usunąć WSZYSTKIE ${relations.length} relacji? Tej operacji nie można cofnąć.`,
                  )
                ) {
                  onDeleteAllRelations();
                }
              }}
              disabled={generating}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Usuń wszystkie
            </Button>
          )}
          <GenerateButton
            onClick={handleGenerateRelations}
            generating={generating && currentStep === "linking"}
            label="Generuj relacje z AI"
            loadingLabel="Generowanie relacji..."
            disabled={!hasNodes || nodes.length < 2}
          />
        </div>
      </div>

      {/* Progress */}
      {statusMessage && (
        <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950 px-4 py-3 text-sm text-blue-700 dark:text-blue-300">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
          <span className="flex-1">{statusMessage}</span>
          {elapsed > 0 && (
            <span className="text-xs text-blue-500 tabular-nums">
              {elapsed}s
            </span>
          )}
        </div>
      )}

      {/* Gating */}
      {!hasNodes && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Najpierw wygeneruj Nodes — relacje lacza istniejace nodes.
          </AlertDescription>
        </Alert>
      )}

      {hasNodes && nodes.length < 2 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Potrzeba minimum 2 nodes aby generowac relacje. Obecnie:{" "}
            {nodes.length} node.
          </AlertDescription>
        </Alert>
      )}

      <RelationsTable
        relations={relations}
        nodes={nodes}
        loading={relationsLoading}
        onDeleteRelation={onDeleteRelation}
        onCreateRelation={onCreateRelation}
      />
    </div>
  );
}
